import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDebouncedCallback } from 'use-debounce';
import { Stack } from '@mui/material';

import { Pagination } from 'components/Pagination';
import { usePageSize } from 'hook/usePageSize';
import { NotFound } from 'components/NotFound/NotFound';
import { Loader } from 'components/Loader';
import { Search } from 'components/Search';
import { DetailViewTabList } from 'components/TabList';
import { useApplications } from '../../api/getApplications';
import { ApplicationListItem } from '../Applications/ApplicationListItem';
import { ApplicationsKanban } from './ApplicationsKanban';

const applicationsTabName = 'applications';
const KANBAN_VIEW = 'kanban';
const LIST_VIEW = 'list';
const KANBAN_LIMIT = 500;

export const ApplicationsTab = ({ currentTab, jobAdID }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = usePageSize();
  const [search, setSearch] = useState('');

  const isActive = currentTab === applicationsTabName;
  const view = searchParams.get('view') === LIST_VIEW ? LIST_VIEW : KANBAN_VIEW;

  const { data: kanbanData, isFetching: kanbanFetching } = useApplications(jobAdID, {
    page: 0,
    limit: KANBAN_LIMIT,
    search,
    queryOptions: { enabled: isActive && view === KANBAN_VIEW },
  });

  const { data: listData, isFetching: listFetching } = useApplications(jobAdID, {
    page: page - 1,
    limit: pageSize,
    search,
    queryOptions: { enabled: isActive && view === LIST_VIEW },
  });

  const handleViewChange = (_, newView) => {
    const params = new URLSearchParams(searchParams);
    params.set('view', newView);
    setSearchParams(params, { replace: true });
  };

  const debouncedSearchChange = useDebouncedCallback((value) => {
    setSearch(value);
    setPage(1);
  }, 300);
  const handleSearchChange = ({ target: { value } }) => debouncedSearchChange(value);

  const handlePageChange = (_, page) => setPage(page);

  if (!isActive) return null;

  const { applications, total } = listData;

  return (
    <Stack gap={2} height={1}>
      <DetailViewTabList
        value={view}
        onChange={handleViewChange}
        tabs={[
          { label: 'Kanban', value: KANBAN_VIEW },
          { label: 'List', value: LIST_VIEW },
        ]}
        multi
      />

      <Search
        handleSearchChange={handleSearchChange}
        initialValue={search}
        sx={{ minWidth: 200, width: 270, flexShrink: 0 }}
      />

      {view === KANBAN_VIEW ? (
        <ApplicationsKanban
          applications={kanbanData.applications}
          total={kanbanData.total}
          isFetching={kanbanFetching}
          isSearching={!!search}
        />
      ) : listFetching && applications.length === 0 ? (
        <Loader />
      ) : (
        <>
          <Stack gap={1} flex={1}>
            {applications.length === 0 && (
              <NotFound
                title="No applications found"
                caption={search ? 'No applications match your search.' : "This ad hasn't received any applications yet."}
                wrapperSx={{ mt: 19 }}
              />
            )}

            {applications.map((application) => (
              <ApplicationListItem key={application.application_id} application={application} showJobAd={false} />
            ))}
          </Stack>

          {applications.length > 0 && (
            <Stack direction="row" justifyContent="center">
              <Pagination
                page={page}
                handlePageChange={handlePageChange}
                itemCount={total}
                itemsPerPage={pageSize}
                onItemsPerPageChange={setPageSize}
              />
            </Stack>
          )}
        </>
      )}
    </Stack>
  );
};
